import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../../firebase';
import { ref as dbRef, push, set, onValue, update, get } from 'firebase/database';
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
    FaSave, 
    FaCheckCircle,
    FaArrowLeft
} from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import EngineerLayout from './EngineerLayout';

const EngineerAddWork = () => {
    const { category, id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [loading, setLoading] = useState(isEditMode);
    const [uploading, setUploading] = useState(false);
    const [files, setFiles] = useState({
        aaUpload: null,
        recapUpload: null,
        tsUpload: null,
        dtpUpload: null,
        estimateUpload: null
    });

    const [formData, setFormData] = useState({
        schemeName: '',
        budgetHead: '',
        taluka: '',
        workName: '',
        tenderAmount: '',
        workType: 'Civil',
        inwardNo: '',
        date: new Date().toISOString().split('T')[0],
        adminApprovalAuthority: '',
        inwardDate: '',
        technicalApprovalOrderNo: '',
        budgetaryProvisionYear: '',
        completionPeriod: '',
        tenderType: '',
        status: 'pending',
        engRemark: '',
        recapitulation: {
            workPortion: 0,
            insurance: 0,
            gst: 0,
            other: 0,
            total: 0
        }
    });

    // Authentication Check
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });
        return () => unsubscribe();
    }, [navigate]);

    // Fetch existing data if in edit mode - supports both WorkNames and generatework paths
    useEffect(() => {
        if (isEditMode && id) {
            const searchParams = new URLSearchParams(location.search);
            const urlDate = searchParams.get('date');

            const fetchData = async () => {
                setLoading(true);
                try {
                    const potentialPaths = [];

                    if (urlDate) {
                        potentialPaths.push(
                            `ekamwatapzp/generatework/${urlDate}/${category || 'General'}/${id}`,
                            `ekamwatapzp/generatework/${urlDate}/${id}`
                        );
                    }

                    potentialPaths.push(
                        `ekamwatapzp/WorkNames/${category || 'General'}/${id}`,
                        `ekamwatapzp/WorkNames/${id}`
                    );

                    let snapshot = null;
                    for (const path of potentialPaths) {
                        snapshot = await get(dbRef(db, path));
                        if (snapshot.exists()) break;
                    }

                    if (snapshot && snapshot.exists()) {
                        const data = snapshot.val();
                        setFormData({
                            ...data,
                            recapitulation: data.recapitulation || {
                                workPortion: 0,
                                insurance: 0,
                                gst: 0,
                                other: 0,
                                total: 0
                            },
                            date: data.date || '',
                            inwardDate: data.inwardDate || ''
                        });
                    } else {
                        toast.error("माहिती सापडली नाही!");
                        navigate(-1);
                    }
                } catch (err) {
                    console.error("Fetch error:", err);
                    toast.error("डेटा लोड करताना त्रुटी आली.");
                } finally {
                    setLoading(false);
                }
            };

            fetchData();
        }
    }, [isEditMode, category, id, location.search, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRecapChange = (e) => {
        const { name, value } = e.target;
        const numValue = parseFloat(value) || 0;
        setFormData(prev => {
            const newRecap = { ...prev.recapitulation, [name]: numValue };
            newRecap.total = Number(newRecap.workPortion) + Number(newRecap.insurance) + Number(newRecap.gst) + Number(newRecap.other);
            return { ...prev, recapitulation: newRecap };
        });
    };

    const handleFileChange = (e) => {
        const { name, files: selectedFiles } = e.target;
        if (selectedFiles[0]) {
            setFiles(prev => ({ ...prev, [name]: selectedFiles[0] }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);

        try {
            const searchParams = new URLSearchParams(location.search);
            const urlDate = searchParams.get('date');

            const uploadedUrls = {};
            for (const [key, file] of Object.entries(files)) {
                if (file) {
                    const storageRef = sRef(storage, `ekamwatapzp/WorkDocuments/${Date.now()}_${key}_${file.name}`);
                    const snap = await uploadBytes(storageRef, file);
                    uploadedUrls[key + 'Url'] = await getDownloadURL(snap.ref);
                }
            }

            const dataToSave = {
                ...formData,
                ...uploadedUrls,
                updatedAt: new Date().toISOString(),
            };

            if (isEditMode) {
                const potentialPaths = [];

                if (urlDate) {
                    potentialPaths.push(
                        `ekamwatapzp/generatework/${urlDate}/${category || 'General'}/${id}`,
                        `ekamwatapzp/generatework/${urlDate}/${id}`
                    );
                }

                potentialPaths.push(
                    `ekamwatapzp/WorkNames/${category || 'General'}/${id}`,
                    `ekamwatapzp/WorkNames/${id}`
                );

                let updatePath = '';
                for (const path of potentialPaths) {
                    const snap = await get(dbRef(db, path));
                    if (snap.exists()) {
                        updatePath = path;
                        break;
                    }
                }

                if (!updatePath) {
                    updatePath = category === 'General' || !category
                        ? `ekamwatapzp/WorkNames/${id}`
                        : `ekamwatapzp/WorkNames/${category}/${id}`;
                }

                await update(dbRef(db, updatePath), dataToSave);
                toast.success("बदल यशस्वीरीत्या जतन केले आहेत!");
                navigate(-1);
            } else {
                const worksRootRef = dbRef(db, 'ekamwatapzp/WorkNames');
                const snapshot = await get(worksRootRef);

                let nextId = 1;
                if (snapshot.exists()) {
                    const allData = snapshot.val();
                    const numericIds = [];
                    Object.entries(allData).forEach(([key, val]) => {
                        if (!isNaN(key)) {
                            numericIds.push(parseInt(key));
                        } else if (val && typeof val === 'object') {
                            Object.keys(val).forEach(subKey => {
                                if (!isNaN(subKey)) numericIds.push(parseInt(subKey));
                            });
                        }
                    });
                    if (numericIds.length > 0) {
                        nextId = Math.max(...numericIds) + 1;
                    }
                }

                const workRef = dbRef(db, `ekamwatapzp/WorkNames/${nextId}`);
                await set(workRef, {
                    ...dataToSave,
                    id: nextId.toString(),
                    category: 'General',
                    createdAt: new Date().toISOString(),
                    status: 'pending',
                    workNumber: `W-${nextId.toString().padStart(6, '0')}`,
                    storedBy: 'Engineer'
                });
                toast.success(`काम यशस्वीरीत्या जतन केले आहे! (ID: ${nextId})`);
                navigate('/engineer/work-names-view');
            }
        } catch (error) {
            console.error("Error saving work:", error);
            toast.error("त्रुटी: " + error.message);
        } finally {
            setUploading(false);
        }
    };

    const schemeOptions = [
        "को. प बंधारे बांधकामे व दुरुस्ती",
        "जलयुक्त शिवार अभियान",
        "यशवंत पंचायत राज अभियान",
        "जिल्हा परिषद स्विय निधी - पशुसंवर्धन पशु वैद्यकिय दवाखाना दुरुस्ती, रंगकाम",
        "पंचायत समिती स्वीय निधी",
        "जि.प.स्विय निधी - जि.प. मालकीच्या खुल्या जागा व्यापारी तत्वावर विकसीत करणे",
        "जि.प.स्विय निधी - ग्रामीण व इतर जिल्हा रस्ते बांधकाम देखभाल व दुरुस्ती",
        "जि.प. स्विय निधी - जि.प. अधिकारी व पदाधिकारी निवासी इमारती देखभाल व दुरुस्ती",
        "जि.प. स्विय निधी - पंचायत समिती इमारत तसेच पंचायत समिती स्तरावरिल मुख्यालयाचे विश्रामधाम व बचतधाम देखभाल व दुरुस्ती व नवीन बांधकाम",
        "जि.प. स्विय निधी - जि.प. इतर इमारती व कॅम्प ऑफिस बांधकाम, देखभाल व दुरुस्ती",
        "जि.प.स्विय निधी - जि.प. मालकीच्या इमारती देखभाल व दुरुस्ती",
        "जि.प स्विय निधी सामुहिक विकास कार्यक्रम",
        "अटल भूजल योजना",
        "जि.प.स्वीय निधी २०२३-२०२४ लघु पाटबंधारे योजना देखभाल व दुरुस्ती",
        "समग्र शिक्षा अभियान (सर्वसाधारण)",
        "आरोग्य उपकेंद्र बांधकाम करणे (बांधकाम व विस्तारीकरण)",
        "विद्युत देखभाल व दुरुस्ती(मुख्यालय)",
        "प्रधानमंत्री खनिज क्षेत्र कल्याण योजना",
        "पंधरावा वित्त आयोग पंचायत समिती स्तर टाईड",
        "पंधरावा वित्त आयोग पंचायत समिती स्तर अनटाईड",
        "पंधरावा वित्त आयोग जिल्हा परिषद स्तर टाईड",
        "पंधरावा वित्त आयोग जिल्हा परिषद स्तर अनटाईड",
        "जि.प.स्वीय निधी शिक्षण शाळा दुरुस्ती",
        "सामुहिक विकास कार्यक्रम जि. प. स्वीय निधी",
        "प.स.स्तरावरील मुख्यालये विश्रामगृह व बचतधाम देखभाल व दुरुस्ती",
        "जिल्हा परिषद स्विय निधी जि.प.इतर इमारती व कॅम्प ऑफीस बांधकाम देखभाल व दुरुस्ती",
        "डॉ.बाबासाहेब आंबेडकर जयंती समाज विकास कार्यक्रम",
        "टेलिफोन खुदाई",
        "इतर ग्रामीण विकास कार्यक्रम ग्रामपंचायत विभाग",
        "सार्वजनिक बांधकाम इमारती दुरुस्ती जुन्या इमारती",
        "आदिवासी विकास प्रकल्प ठक्करबाप्पा आदिवासी वस्ती सुधारणा",
        "रस्ते व पुल दुरुस्ती परिक्षण कार्यक्रम अ/ब/क/ड",
        "निवासी इमारती बांधकाम व दुरुस्ती",
        "प्रशासकीय इमारत बांधकाम",
        "प्रादेशिक पर्यटन विकास",
        "तिर्थक्षेत्र विकास कार्यक्रम (राज्यस्तर ब गट)",
        "पशुवैद्यकीय दवाखाने (प्लॅन)",
        "प्राथमिक आरोग्य केंद्र उपकेंद्र बांधकाम व दुरुस्ती",
        "अंगणवाडी दुरुस्ती",
        "अंगणवाडी नवीन बांधकाम जिल्हा वार्षिक योजना",
        "तिर्थक्षेत्र विकास कार्यक्रम (डीपीडीसी क गट)",
        "किडा विकास कार्यक्रम",
        "जिल्हा वार्षिक योजना जिल्हा परिषद शाळा नवीन इमारत बांधकाम",
        "जिल्हा वार्षिक योजना जिल्हा परिषद शाळा दुरुस्ती",
        "अतिवृष्टी पुरहानी",
        "पर्यटन स्थळासाठी सोई सुविधा पुरविणे",
        "डोंगरी विकास कार्यक्रम",
        "खासदार फंड",
        "आमदार फंड",
        "इतर जिल्हा रस्ते विकास व मजबुतीकरण कार्यक्रम 5054 4308-31",
        "ग्रामीण रस्ते विकास व मजबुतीकरण कार्यक्रम 3054 2061-31"
    ];

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
                    <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-bold animate-pulse">माहिती लोड होत आहे...</p>
                </div>
            </EngineerLayout>
        );
    }

    const inputClass = "w-full h-11 border border-gray-300 rounded-md px-3 text-[14px] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white shadow-sm font-medium";
    const labelClass = "block text-sm font-bold text-gray-700 transition-colors group-hover:text-blue-600";

    return (
        <EngineerLayout>
            <div className="bg-[#f0f2f5] min-h-screen pb-12">
                {/* Header */}
                <div className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 mb-6">
                    <div className="max-w-7xl mx-auto flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate(-1)}
                                className="bg-gray-100 p-2 rounded-lg text-gray-400 hover:text-blue-600 transition hover:bg-white border border-transparent hover:border-gray-100 shadow-sm"
                                title="मागे जा"
                            >
                                <FaArrowLeft size={14} />
                            </button>
                            <h1 className="text-xl font-bold text-gray-800 tracking-tight">
                                {isEditMode ? "कामात बदल करा" : "नवीन कामाची नोंदणी"}
                            </h1>
                        </div>
                        <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400 select-none uppercase tracking-wider">
                            <span className="hover:text-blue-600 transition cursor-pointer">सांगली जिल्हा परिषद</span>
                            <span>/</span>
                            <span className="text-[#0091ea]">{isEditMode ? "बदल करा" : "कामाची माहिती"}</span>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-6">
                    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-xl border border-gray-100 p-8 sm:p-10">

                        {/* Basic Info - 2 Columns */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mb-10">
                            {[
                                { label: 'योजनेचे नाव', name: 'schemeName', type: 'select', options: schemeOptions },
                                { label: 'लेखाशिर्ष', name: 'budgetHead', type: 'text' },
                                { label: 'तालुका', name: 'taluka', type: 'select', options: ["मिरज", "तासगाव", "वाळवा", "शिराळा", "खानापूर", "आटपाडी", "जत", "कवठेमहांकाळ", "कडेगाव"] },
                                { label: 'कामाचे नाव', name: 'workName', type: 'text' },
                                { label: 'निविदा रक्कम', name: 'tenderAmount', type: 'number' },
                                { label: 'वर्क टाईप', name: 'workType', type: 'select', options: ['Civil', 'Electrical', 'Plant'] },
                                { label: 'जावक नंबर / प्रमा नंबर / दिनांक', name: 'inwardNo', type: 'text' },
                                { label: 'दिनांक', name: 'date', type: 'date' }
                            ].map((field) => (
                                <div key={field.name} className="space-y-1.5 flex flex-col group">
                                    <label className={labelClass}>
                                        {field.label} <span className="text-red-500 font-extrabold">*</span>
                                    </label>
                                    {field.type === 'select' ? (
                                        <select
                                            name={field.name}
                                            required
                                            value={formData[field.name] || ''}
                                            onChange={handleInputChange}
                                            className={inputClass}
                                        >
                                            <option value="">{field.label === 'योजनेचे नाव' ? 'कृपया योजनेचे नाव निवडा' : 'निवडा'}</option>
                                            {field.options.map((opt, i) => (
                                                <option key={i} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type={field.type}
                                            name={field.name}
                                            required
                                            value={formData[field.name] || ''}
                                            onChange={handleInputChange}
                                            placeholder={field.label}
                                            className={inputClass}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="h-px bg-gray-200 w-full mb-10" />

                        {/* Technical Info - 3 Columns */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                            {[
                                { label: 'प्रशासकीय मंजुरीचा अधिकार', name: 'adminApprovalAuthority', type: 'text' },
                                { label: 'जावक.क्र दिनांक', name: 'inwardDate', type: 'date' },
                                { label: 'तांत्रिक मंजूर आदेश क्र.', name: 'technicalApprovalOrderNo', type: 'text' },
                                { label: 'अंदाजपत्रकीय तरतूद सन', name: 'budgetaryProvisionYear', type: 'text' },
                                { label: 'काम पुर्ण करणेची मुदत', name: 'completionPeriod', type: 'text' },
                                { label: 'टेंडरचा प्रकार', name: 'tenderType', type: 'text' }
                            ].map((field) => (
                                <div key={field.name} className="space-y-1.5 flex flex-col group">
                                    <label className={labelClass}>
                                        {field.label} <span className="text-red-500 font-extrabold">*</span>
                                    </label>
                                    <input
                                        type={field.type}
                                        name={field.name}
                                        required
                                        value={formData[field.name] || ''}
                                        onChange={handleInputChange}
                                        className={inputClass}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Recapitulation Table */}
                        <div className="bg-[#fcfdff] border border-gray-200 rounded overflow-hidden mb-10 shadow-sm">
                            <div className="bg-[#f8fafc] border-b border-gray-200 px-6 py-4 font-black text-gray-700 text-sm tracking-wide uppercase">
                                Recapitulation
                            </div>
                            <table className="w-full border-collapse">
                                <tbody className="divide-y divide-gray-200">
                                    {[
                                        { label: 'Work Portion', name: 'workPortion' },
                                        { label: 'Insurance', name: 'insurance' },
                                        { label: 'GST', name: 'gst' },
                                        { label: 'Other', name: 'other' },
                                        { label: 'Total', name: 'total', readOnly: true }
                                    ].map((item, idx) => (
                                        <tr key={item.name} className="hover:bg-blue-50/30 transition-colors">
                                            <td className="w-16 px-6 py-3 border-r border-gray-200 font-black text-gray-800 text-center bg-gray-50/50 text-xs">{idx + 1}</td>
                                            <td className="px-6 py-3 border-r border-gray-200 w-1/3 font-bold text-gray-600 text-[13px]">{item.label}</td>
                                            <td className="px-6 py-2 bg-white">
                                                <input
                                                    type="number"
                                                    name={item.name}
                                                    value={formData.recapitulation?.[item.name] || 0}
                                                    onChange={handleRecapChange}
                                                    readOnly={item.readOnly}
                                                    className={`w-full py-2 px-3 border rounded text-sm transition outline-none font-bold ${item.readOnly ? 'bg-[#f1f5f9] border-gray-300 cursor-not-allowed text-[#1a1c23]' : 'border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100/50'}`}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Document Upload Table */}
                        <div className="bg-[#fcfdff] border border-gray-200 rounded overflow-hidden mb-10 shadow-sm">
                            <div className="bg-[#f8fafc] border-b border-gray-200 px-6 py-4 font-black text-gray-700 text-sm tracking-wide uppercase">
                                Upload Document
                            </div>
                            <table className="w-full border-collapse">
                                <tbody className="divide-y divide-gray-200">
                                    {[
                                        { label: 'AA Upload (फक्त 5MB PDF अपलोड करा)', name: 'aaUpload' },
                                        { label: 'Recap Upload (फक्त 5MB PDF अपलोड करा)', name: 'recapUpload' },
                                        { label: 'TS Upload (फक्त 5MB PDF अपलोड करा)', name: 'tsUpload' },
                                        { label: 'DTP Upload (फक्त 5MB PDF अपलोड करा)', name: 'dtpUpload' },
                                        { label: 'Estimate Upload (फक्त 5MB PDF अपलोड करा)', name: 'estimateUpload' }
                                    ].map((item, idx) => (
                                        <tr key={item.name} className="hover:bg-blue-50/30 transition-colors">
                                            <td className="w-16 px-6 py-3 border-r border-gray-200 font-black text-gray-800 text-center bg-gray-50/50 text-xs">{idx + 1}</td>
                                            <td className="px-6 py-3 border-r border-gray-200 w-1/3 font-bold text-gray-600 text-[13px]">{item.label}</td>
                                            <td className="px-6 py-3 bg-white">
                                                <div className="flex items-center gap-8">
                                                    <div className="relative group w-full max-w-[280px]">
                                                        <div className="flex items-center border border-gray-300 rounded overflow-hidden bg-white h-9 shadow-sm transition group-hover:border-blue-400">
                                                            <div className="bg-[#f8f9fa] border-r border-gray-300 px-3 h-full flex items-center text-[10px] font-black text-gray-500 group-hover:bg-blue-50 transition whitespace-nowrap uppercase tracking-tighter">
                                                                Choose file
                                                            </div>
                                                            <div className="px-3 text-[11px] text-gray-400 truncate flex-grow font-medium">
                                                                {files[item.name] ? files[item.name].name : (formData[item.name + 'Url'] ? "File Already Uploaded" : "No file chosen")}
                                                            </div>
                                                        </div>
                                                        <input
                                                            type="file"
                                                            name={item.name}
                                                            accept=".pdf"
                                                            onChange={handleFileChange}
                                                            className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                                        />
                                                    </div>
                                                    {(formData[item.name + 'Url'] || files[item.name]) && (
                                                        <div className="flex items-center gap-3">
                                                            {formData[item.name + 'Url'] && !files[item.name] && (
                                                                <a
                                                                    href={formData[item.name + 'Url']}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-[#0091ea] hover:text-blue-700 text-[11px] font-black tracking-tight transition hover:underline whitespace-nowrap uppercase"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    View Document
                                                                </a>
                                                            )}
                                                            <FaCheckCircle className="text-green-500 text-base" />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Status & Remark (Engineer-specific) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 mb-10">
                            <div className="space-y-1.5 flex flex-col group">
                                <label className={labelClass}>कामाची स्थिती (Status)</label>
                                <select
                                    name="status"
                                    value={formData.status || 'pending'}
                                    onChange={handleInputChange}
                                    className={inputClass}
                                >
                                    <option value="pending">प्रलंबित (Pending)</option>
                                    <option value="Approved">मंजूर (Approved)</option>
                                    <option value="Rejected">नाकारले (Rejected)</option>
                                </select>
                            </div>
                            <div className="space-y-1.5 flex flex-col group">
                                <label className={labelClass}>Eng. रिमार्क</label>
                                <textarea
                                    name="engRemark"
                                    value={formData.engRemark || ''}
                                    onChange={handleInputChange}
                                    placeholder="काही टिपणी असल्यास लिहा..."
                                    rows={3}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-[14px] focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition bg-white shadow-sm font-medium resize-none"
                                />
                            </div>
                        </div>

                        {/* Submit Button */}
                        <div className="mt-8 flex justify-start border-t border-gray-100 pt-10">
                            <button
                                type="submit"
                                disabled={uploading}
                                className="bg-[#5c67f2] hover:bg-[#4a54d1] text-white px-10 py-2.5 rounded-md shadow-[0_10px_20px_-5px_rgba(92,103,242,0.4)] transition-all transform active:scale-95 disabled:opacity-50 flex items-center gap-3 font-bold text-[14px] uppercase tracking-wide"
                            >
                                {uploading ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>{isEditMode ? "बदल जतन करा" : "Submit"}</>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerAddWork;
